import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Grid,
  TextField,
  CircularProgress,
  Typography,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  TablePagination,
  Tooltip,
  Autocomplete,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  Paper,
  IconButton,
  Collapse
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import { useAuth } from '../context/AuthContext';
import { useJob } from '../context/JobContext';
import { toast } from 'react-toastify';
import api from '../services/api';
import { CalendarDays as SintesiLogoIcon } from 'lucide-react';

function AttivitaSintesiMensileInner() {
  const { user } = useAuth();
  const { addJob } = useJob();
  const roleName = (user?.role_name || user?.role || '').toLowerCase();
  const isAdmin = roleName === 'admin';

  const [month, setMonth] = useState(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  });
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [loadingDay, setLoadingDay] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [calendar, setCalendar] = useState({ days: [] });
  const [selectedDate, setSelectedDate] = useState('');
  const [dayActivities, setDayActivities] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [targetsInfo, setTargetsInfo] = useState(null);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [adminSummaryOpen, setAdminSummaryOpen] = useState(false);
  const [adminSummaryData, setAdminSummaryData] = useState([]);
  const [adminSummaryError, setAdminSummaryError] = useState(null);
  const [loadingAdminSummary, setLoadingAdminSummary] = useState(false);

  const formatDateIt = (value) => {
    if (!value) return '';
    const iso = String(value).slice(0, 10);
    const parts = iso.split('-');
    if (parts.length !== 3) return String(value);
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const getCategoryKeyByName = (name) => {
    const n = (name || '').toLowerCase();
    if (n.startsWith('in regola')) return 'IN_REGOLA';
    if (n.startsWith('attenzione')) return 'ATTENZIONE';
    if (n.startsWith('critica')) return 'CRITICA';
    if (n.startsWith('senza note') || n.startsWith('senza nota')) return 'SENZA_NOTE';
    return null;
  };

  const fetchUsersForMonth = async (m) => {
    setLoadingUsers(true);
    try {
      if (!isAdmin) {
        if (user?.id) {
          setUsers([]);
          setSelectedUserId(user.id);
        } else {
          setSelectedUserId('');
          setCalendar({ days: [] });
          setSelectedDate('');
          setDayActivities([]);
        }
      } else {
        const res = await api.get('/attivita/registro/sintesi', {
          params: { month: m, users_only: 'true' }
        });
        const data = Array.isArray(res.data) ? res.data : [];
        const sorted = [...data].sort((a, b) => {
          const labelA = (a.user_name || `User ${a.user_id}`).toLocaleLowerCase('it-IT');
          const labelB = (b.user_name || `User ${b.user_id}`).toLocaleLowerCase('it-IT');
          return labelA.localeCompare(labelB, 'it', { sensitivity: 'base' });
        });
        setUsers(sorted);
        if (sorted.length > 0) {
          setSelectedUserId((prev) => (prev ? prev : sorted[0].user_id));
        } else {
          setSelectedUserId('');
          setCalendar({ days: [] });
          setSelectedDate('');
          setDayActivities([]);
        }
      }
    } catch (e) {
      console.error('Errore caricamento utenti per sintesi mensile:', e);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchEffectiveTargets = async (dateValue, userIdValue) => {
    if (!userIdValue) {
      setTargetsInfo(null);
      return;
    }
    try {
      setLoadingTargets(true);
      const params = { user_id: userIdValue };
      if (dateValue) params.date = dateValue;
      const res = await api.get('/attivita/stato-iniziale-targets/effective', { params });
      const data = res.data || {};
      const perCategory = data.perCategory || {
        IN_REGOLA: null,
        ATTENZIONE: null,
        CRITICA: null,
        SENZA_NOTE: null
      };
      setTargetsInfo({
        mainTarget: Number.isFinite(Number(data.mainTarget)) ? Number(data.mainTarget) : 0,
        perCategory,
        allTargets: Array.isArray(data.rows)
          ? data.rows.map((r) => ({
              key: getCategoryKeyByName(r.stato_iniziale_name),
              name: r.stato_iniziale_name,
              value: Number(r.daily_target),
              source: r.source,
              valid_from: r.valid_from || null,
              valid_to: r.valid_to || null
            }))
          : []
      });
    } catch (e) {
      console.error('Errore caricamento obiettivi stato iniziale:', e);
    } finally {
      setLoadingTargets(false);
    }
  };

  const fetchCalendar = async (m, userId) => {
    if (!userId) {
      setCalendar({ days: [] });
      return;
    }
    setLoadingCalendar(true);
    try {
      const res = await api.get('/attivita/registro/calendar', { params: { month: m, userId } });
      setCalendar(res.data || { days: [] });
      setSelectedDate('');
      setDayActivities([]);
    } catch (e) {
      console.error('Errore caricamento calendario attività:', e);
    } finally {
      setLoadingCalendar(false);
    }
  };

  const fetchDayActivities = async (date, userId) => {
    if (!date || !userId) {
      setDayActivities([]);
      return;
    }
    setLoadingDay(true);
    try {
      const res = await api.get('/attivita/registro/giorno-attivita', {
        params: { date, userId }
      });
      const data = Array.isArray(res.data) ? res.data : [];
      setDayActivities(data);
    } catch (e) {
      console.error('Errore caricamento attività del giorno:', e);
    } finally {
      setLoadingDay(false);
    }
  };

  const fetchAdminSummary = async (date) => {
    if (!date) return;
    setLoadingAdminSummary(true);
    setAdminSummaryError(null);
    setAdminSummaryData([]);
    try {
      const res = await api.get('/attivita/registro/daily-summary', { params: { date } });
      setAdminSummaryData(res.data || []);
    } catch (e) {
      console.error('Errore caricamento riepilogo admin:', e);
      setAdminSummaryError('Errore durante il caricamento dei dati. Assicurati di avere i permessi necessari.');
    } finally {
      setLoadingAdminSummary(false);
    }
  };

  const handleAdminSummaryClick = () => {
    if (!selectedDate) {
      alert('Seleziona prima un giorno dal calendario.');
      return;
    }
    setAdminSummaryOpen(true);
    fetchAdminSummary(selectedDate);
  };

  const handleGeneratePdf = async () => {
    if (!selectedDate) return;
    try {
      const response = await api.post('/attivita/registro/report', { date: selectedDate });
      const { jobId } = response.data;
      addJob(jobId, 'admin_activity_summary');
    } catch (error) {
      console.error('Error requesting PDF:', error);
      toast.error('Errore durante la richiesta del PDF');
    }
  };

  useEffect(() => {
    fetchUsersForMonth(month);
  }, [month]);

  useEffect(() => {
    fetchEffectiveTargets(selectedDate, selectedUserId);
  }, [selectedDate, selectedUserId]);

  useEffect(() => {
    if (selectedUserId) {
      fetchCalendar(month, selectedUserId);
    }
  }, [month, selectedUserId]);

  const handleSelectDay = (date) => {
    setSelectedDate(date);
    setPage(0);
    fetchDayActivities(date, selectedUserId);
  };

  const calendarMap = useMemo(() => {
    const map = new Map();
    if (calendar && Array.isArray(calendar.days)) {
      calendar.days.forEach((d) => {
        map.set(d.day, d);
      });
    }
    return map;
  }, [calendar]);

  const weeks = useMemo(() => {
    const [yearStr, monthStr] = month.split('-');
    const year = parseInt(yearStr, 10);
    const m = parseInt(monthStr, 10) - 1;
    const first = new Date(year, m, 1);
    const daysInMonth = new Date(year, m + 1, 0).getDate();
    const startWeekday = (first.getDay() + 6) % 7; // 0 = Monday
    const cells = [];
    for (let i = 0; i < startWeekday; i++) {
      cells.push(null);
    }
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(d);
    }
    while (cells.length % 7 !== 0) {
      cells.push(null);
    }
    const result = [];
    for (let i = 0; i < cells.length; i += 7) {
      result.push(cells.slice(i, i + 7));
    }
    return result;
  }, [month]);

  const currentUser = useMemo(
    () => users.find((u) => u.user_id === selectedUserId),
    [users, selectedUserId]
  );

  const getDayStatus = (dayNumber) => {
    const entry = calendarMap.get(dayNumber);
    return entry ? entry.status : 'NONE';
  };

  const getStatusColor = (status) => {
    if (status === 'OK') return 'success.main';
    if (status === 'KO') return 'error.main';
    if (status === 'BLOCK') return 'warning.main';
    return 'action.disabledBackground';
  };

  const getStatusLabel = (status) => {
    if (status === 'OK') return '🙂';
    if (status === 'KO') return '☹️';
    if (status === 'BLOCK') return 'Bloccante';
    return 'Nessuna attività';
  };

  const getDayTooltip = (dayNumber) => {
    const entry = calendarMap.get(dayNumber);
    if (!entry) {
      return 'Nessuna attività registrata';
    }
    const total = entry.total_activities || 0;
    const blocking = entry.blocking_activities || 0;
    const okTargets = entry.targets_ok || 0;
    const koTargets = entry.targets_ko || 0;
    if (entry.status === 'BLOCK') {
      return `Bloccante perché hai svolto ${blocking} attività su tipologie bloccanti (totale ${total} attività).`;
    }
    const trackedTargets = okTargets + koTargets;
    if (entry.status === 'OK') {
      if (trackedTargets > 0) {
        return `Obiettivi raggiunti: ${okTargets} tipologie in target su ${trackedTargets} (totale ${total} attività).`;
      }
      return `Giornata in regola con gli obiettivi (totale ${total} attività).`;
    }
    if (entry.status === 'KO') {
      if (trackedTargets > 0) {
        return `Obiettivi non raggiunti: ${koTargets} tipologie sotto soglia su ${trackedTargets} (totale ${total} attività).`;
      }
      return `Obiettivi non raggiunti rispetto ai parametri indicati (totale ${total} attività).`;
    }
    if (total === 0) {
      return 'Nessuna attività registrata per questo giorno.';
    }
    return `Totale ${total} attività registrate.`;
  };

  const selectedDayNumber = useMemo(() => {
    if (!selectedDate) return null;
    const parts = selectedDate.split('-');
    return parseInt(parts[2], 10);
  }, [selectedDate]);

  const selectedDayStatus = useMemo(() => {
    if (!selectedDayNumber) return null;
    return getDayStatus(selectedDayNumber);
  }, [selectedDayNumber, calendarMap]);

  const selectedDateLabel = useMemo(() => {
    if (!selectedDate) return '';
    const parts = selectedDate.split('-');
    if (parts.length !== 3) return selectedDate;
    const year = parseInt(parts[0], 10);
    const monthIndex = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    const dateObj = new Date(year, monthIndex, day);
    const formattedDate = dateObj.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const weekday = dateObj
      .toLocaleDateString('it-IT', { weekday: 'long' })
      .toLowerCase();
    return `Attività del ${formattedDate} ${weekday}`;
  }, [selectedDate]);

  const summaryMatrix = useMemo(() => {
    if (!calendar || !Array.isArray(calendar.days) || calendar.days.length === 0) {
      return null;
    }

    const emptyCounts = {
      IN_REGOLA: 0,
      ATTENZIONE: 0,
      CRITICA: 0,
      SENZA_NOTE: 0
    };
    const dailyAverage = { ...emptyCounts };
    const weeklyAverage = { ...emptyCounts };
    const monthlyTotal = { ...emptyCounts };

    const [yearStr, monthStr] = month.split('-');
    const year = parseInt(yearStr, 10);
    const monthIndex = parseInt(monthStr, 10) - 1;
    const first = new Date(year, monthIndex, 1);
    const startWeekday = (first.getDay() + 6) % 7;

    const weekBuckets = new Map();
    const isWeekend = (dayObj) => {
      if (!dayObj || !dayObj.date) return false;
      const date = new Date(dayObj.date);
      if (Number.isNaN(date.getTime())) return false;
      const dow = date.getDay(); // 0 = Sunday, 6 = Saturday
      return dow === 0 || dow === 6;
    };

    let workdayCount = 0;
    calendar.days.forEach((d) => {
      if (isWeekend(d)) {
        return;
      }
      workdayCount += 1;

      const categoryCounts = d.category_counts || emptyCounts;
      Object.keys(emptyCounts).forEach((key) => {
        const value = Number(categoryCounts[key]) || 0;
        monthlyTotal[key] += value;
      });

      const idx = startWeekday + (d.day - 1);
      const weekIndex = Math.floor(idx / 7);
      if (!weekBuckets.has(weekIndex)) {
        weekBuckets.set(weekIndex, { counts: { ...emptyCounts }, workdays: 0 });
      }
      const weekEntry = weekBuckets.get(weekIndex);
      weekEntry.workdays += 1;
      Object.keys(emptyCounts).forEach((key) => {
        const value = Number(categoryCounts[key]) || 0;
        weekEntry.counts[key] += value;
      });
    });

    Object.keys(emptyCounts).forEach((key) => {
      dailyAverage[key] =
        workdayCount > 0 ? Number((monthlyTotal[key] / workdayCount).toFixed(2)) : 0;
    });

    const weekCount = weekBuckets.size;
    weekBuckets.forEach((entry) => {
      Object.keys(emptyCounts).forEach((key) => {
        weeklyAverage[key] += Number(entry.counts[key]) || 0;
      });
    });
    Object.keys(emptyCounts).forEach((key) => {
      weeklyAverage[key] =
        weekCount > 0 ? Number((weeklyAverage[key] / weekCount).toFixed(2)) : 0;
    });

    const totalDaily = Object.values(dailyAverage).reduce((sum, v) => sum + v, 0);
    const totalWeekly = Object.values(weeklyAverage).reduce((sum, v) => sum + v, 0);
    const totalMonthly = Object.values(monthlyTotal).reduce((sum, v) => sum + v, 0);

    return {
      dailyAverage,
      weeklyAverage,
      monthlyTotal,
      workdayCount,
      weekCount,
      totals: {
        daily: Number(totalDaily.toFixed(2)),
        weekly: Number(totalWeekly.toFixed(2)),
        monthly: totalMonthly
      }
    };
  }, [calendar, month]);

  const currentUserName =
    currentUser?.user_name ||
    user?.name ||
    user?.email ||
    (selectedUserId ? `User ${selectedUserId}` : '');

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <SintesiLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Sintesi Mensile Attività
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Calendario e riepilogo obiettivi per utente sul mese selezionato
            </Typography>
          </Box>
        </Box>
      </Box>

      <Card sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Mese"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ flexGrow: 1, minWidth: { xs: '100%', md: 320 } }}>
                {isAdmin ? (
                  <Autocomplete
                    fullWidth
                    options={users}
                    loading={loadingUsers}
                    value={users.find((u) => u.user_id === selectedUserId) || null}
                    onChange={(event, newValue) => {
                      setSelectedUserId(newValue ? newValue.user_id : '');
                    }}
                    getOptionLabel={(option) => option.user_name || `User ${option.user_id}`}
                    isOptionEqualToValue={(option, value) => option.user_id === value.user_id}
                    disabled={loadingUsers || users.length === 0}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Utente"
                        InputLabelProps={{ shrink: true }}
                        placeholder="Cerca utente..."
                      />
                    )}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label="Utente"
                    value={currentUserName}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                  />
                )}
              </Box>
              <Button
                variant="outlined"
                onClick={() => setSummaryOpen(true)}
                disabled={!summaryMatrix || loadingCalendar || !selectedUserId}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Riepilogo
              </Button>
              {isAdmin && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAdminSummaryClick}
                  sx={{ whiteSpace: 'nowrap', ml: 1 }}
                >
                  Quadro attività
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Card>

      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={5}>
          <Card sx={{ p: 2, mb: 2, height: '100%' }}>
            {(loadingCalendar || loadingUsers) ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Calendario obiettivi
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip size="small" label="🙂" sx={{ bgcolor: 'success.main', color: 'common.white' }} />
                    <Chip size="small" label="☹️" sx={{ bgcolor: 'error.main', color: 'common.white' }} />
                    <Chip size="small" label="Bloccante" sx={{ bgcolor: 'warning.main', color: 'common.white' }} />
                    <Chip size="small" label="Nessuna attività" sx={{ bgcolor: 'action.disabledBackground', color: 'text.primary' }} />
                  </Box>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 1 }}>
                  {['L', 'M', 'M', 'G', 'V', 'S', 'D'].map((d) => (
                    <Box key={d} sx={{ textAlign: 'center', fontWeight: 'bold', color: 'text.secondary' }}>
                      {d}
                    </Box>
                  ))}
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
                  {weeks.flat().map((day, idx) => {
                    if (!day) {
                      return <Box key={`empty-${idx}`} />;
                    }
                    const status = getDayStatus(day);
                    const isSelected = selectedDayNumber === day;
                    return (
                      <Tooltip key={day} title={getDayTooltip(day)} arrow>
                        <Box
                          onClick={() => handleSelectDay(calendarMap.get(day)?.date)}
                          sx={{
                            borderRadius: 1,
                            p: 1,
                            textAlign: 'center',
                            cursor: 'pointer',
                            bgcolor: 'action.hover',
                            color: 'text.primary',
                            border: isSelected ? '2px solid' : '1px solid',
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            boxShadow: isSelected ? 3 : 0
                          }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {day}
                            </Typography>
                            {status !== 'NONE' && (
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  bgcolor: getStatusColor(status),
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'common.white',
                                  fontSize: 14
                                }}
                              >
                                {status === 'OK' ? '🙂' : status === 'KO' ? '☹️' : status === 'BLOCK' ? '⚠️' : ''}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ p: 2, height: '100%' }}>
            {loadingDay ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {selectedDate
                    ? `${selectedDateLabel} — ${currentUserName}`
                    : 'Seleziona un giorno dal calendario'}
                </Typography>
                {selectedDate && (
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={getStatusLabel(selectedDayStatus)}
                      color={
                        selectedDayStatus === 'OK'
                          ? 'success'
                          : selectedDayStatus === 'KO'
                          ? 'error'
                          : selectedDayStatus === 'BLOCK'
                          ? 'warning'
                          : 'default'
                      }
                      size="small"
                    />
                  </Box>
                )}
                {selectedDate && dayActivities.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Nessuna attività registrata per questo giorno.
                  </Typography>
                )}
                {selectedDate && dayActivities.length > 0 && (
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 980 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Ora</TableCell>
                          <TableCell>Cliente</TableCell>
                          <TableCell>Creditore</TableCell>
                          <TableCell>Stato iniziale</TableCell>
                          <TableCell>Attività svolta</TableCell>
                          <TableCell>Stato finale</TableCell>
                          <TableCell>Nota</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dayActivities
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((a) => {
                          const raw = a.operation_date || '';
                          let timeStr = '';
                          if (raw.includes('T')) {
                            timeStr = raw.slice(11, 16);
                          } else if (raw.length >= 16) {
                            timeStr = raw.slice(11, 16);
                          }
                          return (
                            <TableRow key={a.id}>
                              <TableCell>{timeStr}</TableCell>
                              <TableCell>{a.customer_name || a.customer_id || '-'}</TableCell>
                              <TableCell>{a.issuer_name || a.issuer_id || '-'}</TableCell>
                              <TableCell>{a.stato_iniziale_name || '-'}</TableCell>
                              <TableCell>{a.attivita_svolta_name || '-'}</TableCell>
                              <TableCell>{a.stato_finale_name || '-'}</TableCell>
                              <TableCell>{a.nota || '-'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                {selectedDate && dayActivities.length > 0 && (
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={dayActivities.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(parseInt(event.target.value, 10));
                      setPage(0);
                    }}
                    labelRowsPerPage="Righe per pagina:"
                  />
                )}
              </>
            )}
          </Card>
        </Grid>
      </Grid>

      {summaryMatrix && (
        <Dialog
          open={summaryOpen}
          onClose={() => setSummaryOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            Riepilogo stato obiettivi
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Mese {month} — Utente {currentUserName}
            </Typography>
            {targetsInfo && targetsInfo.mainTarget != null && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                Obiettivo giornaliero configurato: almeno {targetsInfo.mainTarget} pratiche
                {' '}(somma delle pratiche che ricadono in In regola, Attenzione, Critica, Senza note)
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Metrica pratiche: Giornaliero e Settimanale sono medie, Mensile è totale.
              Confronto obiettivi: giornaliero vs target, settimanale vs target x 5 giorni lavorativi,
              mensile vs target x giorni lavorativi del mese.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>Obiettivo giornaliero</TableCell>
                  <TableCell align="right">Giornaliero (media)</TableCell>
                  <TableCell align="right">Settimanale (media)</TableCell>
                  <TableCell align="right">Mensile</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  {
                    key: 'IN_REGOLA',
                    label: 'In regola'
                  },
                  {
                    key: 'ATTENZIONE',
                    label: 'Attenzione'
                  },
                  {
                    key: 'CRITICA',
                    label: 'Critica'
                  },
                  {
                    key: 'SENZA_NOTE',
                    label: 'Senza note'
                  }
                ].map((row) => {
                  const targetRule = Array.isArray(targetsInfo?.allTargets)
                    ? targetsInfo.allTargets.find((t) => t.key === row.key)
                    : null;
                  const targetValue =
                    targetsInfo && targetsInfo.perCategory && targetsInfo.perCategory[row.key] != null
                      ? targetsInfo.perCategory[row.key]
                      : targetsInfo && targetsInfo.mainTarget != null
                        ? targetsInfo.mainTarget
                        : null;

                  const dailyValue = summaryMatrix.dailyAverage[row.key] || 0;
                  const weeklyValue = summaryMatrix.weeklyAverage[row.key] || 0;
                  const monthlyValue = summaryMatrix.monthlyTotal[row.key] || 0;

                  const dailyTarget = targetValue != null ? targetValue : null;
                  const weeklyTarget = targetValue != null ? targetValue * 5 : null;
                  const monthlyTarget =
                    targetValue != null ? targetValue * (summaryMatrix.workdayCount || 0) : null;

                  const dailyOk = dailyTarget != null && dailyValue >= dailyTarget;
                  const weeklyOk = weeklyTarget != null && weeklyValue >= weeklyTarget;
                  const monthlyOk = monthlyTarget != null && monthlyValue >= monthlyTarget;

                  return (
                    <TableRow key={row.key}>
                      <TableCell>{row.label}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                          <span>{targetValue != null ? targetValue : '-'}</span>
                          {targetRule && (
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, textAlign: 'center' }}>
                              {targetRule.source === 'default'
                                ? 'Regola standard'
                                : `Regola valida dal ${formatDateIt(targetRule.valid_from)} al ${targetRule.valid_to ? formatDateIt(targetRule.valid_to) : '∞'}`}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          <span>{dailyValue.toFixed(2)}</span>
                          {dailyTarget != null && (dailyOk ? <CheckCircleIcon color="success" fontSize="small" /> : <CancelIcon color="error" fontSize="small" />)}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          <span>{weeklyValue.toFixed(2)}</span>
                          {weeklyTarget != null && (weeklyOk ? <CheckCircleIcon color="success" fontSize="small" /> : <CancelIcon color="error" fontSize="small" />)}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          <span>{monthlyValue}</span>
                          {monthlyTarget != null && (monthlyOk ? <CheckCircleIcon color="success" fontSize="small" /> : <CancelIcon color="error" fontSize="small" />)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Totale</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    {targetsInfo && targetsInfo.mainTarget != null ? targetsInfo.mainTarget : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {summaryMatrix.totals.daily}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {summaryMatrix.totals.weekly}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {summaryMatrix.totals.monthly}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSummaryOpen(false)}>Chiudi</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Admin Daily Summary Dialog */}
      <Dialog
        open={adminSummaryOpen}
        onClose={() => setAdminSummaryOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: { maxWidth: '95vw', maxHeight: '90vh' } }}
      >
        <DialogTitle>
          Quadro {selectedDateLabel ? selectedDateLabel.replace(/^Attività /, '') : ''}
        </DialogTitle>
        <DialogContent dividers>
          {loadingAdminSummary ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : adminSummaryError ? (
            <Box sx={{ p: 2, color: 'error.main', textAlign: 'center' }}>
              <Typography>{adminSummaryError}</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: '75vh', overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell rowSpan={2} sx={{ fontWeight: 'bold', zIndex: 10, left: 0, bgcolor: 'background.paper', minWidth: 150 }}>
                      Utente
                    </TableCell>
                    <TableCell colSpan={4} align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.2)' : '#f1f8e9', fontWeight: 'bold', borderLeft: 1, borderColor: 'divider' }}>
                      In Regola
                    </TableCell>
                    <TableCell colSpan={4} align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(237, 108, 2, 0.2)' : '#fff3e0', fontWeight: 'bold', borderLeft: 1, borderColor: 'divider' }}>
                      Attenzione
                    </TableCell>
                    <TableCell colSpan={4} align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.2)' : '#ffebee', fontWeight: 'bold', borderLeft: 1, borderColor: 'divider' }}>
                      Critica
                    </TableCell>
                    <TableCell colSpan={4} align="center" sx={{ fontWeight: 'bold', borderLeft: 1, borderColor: 'divider' }}>
                      Senza Note
                    </TableCell>
                    <TableCell colSpan={4} align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'action.hover' : '#f5f5f5', fontWeight: 'bold', borderLeft: 2, borderColor: 'divider' }}>
                      Totale
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    {/* In Regola Sub-headers */}
                    <TableCell align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.2)' : '#f1f8e9', borderLeft: 1, borderColor: 'divider' }}>Target</TableCell>
                    <TableCell align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.2)' : '#f1f8e9' }}>Oggi</TableCell>
                    <TableCell align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.2)' : '#f1f8e9' }}>Sett</TableCell>
                    <TableCell align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.2)' : '#f1f8e9' }}>Mese</TableCell>
                    
                    {/* Attenzione Sub-headers */}
                    <TableCell align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(237, 108, 2, 0.2)' : '#fff3e0', borderLeft: 1, borderColor: 'divider' }}>Target</TableCell>
                    <TableCell align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(237, 108, 2, 0.2)' : '#fff3e0' }}>Oggi</TableCell>
                    <TableCell align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(237, 108, 2, 0.2)' : '#fff3e0' }}>Sett</TableCell>
                    <TableCell align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(237, 108, 2, 0.2)' : '#fff3e0' }}>Mese</TableCell>
                    
                    {/* Critica Sub-headers */}
                    <TableCell align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.2)' : '#ffebee', borderLeft: 1, borderColor: 'divider' }}>Target</TableCell>
                    <TableCell align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.2)' : '#ffebee' }}>Oggi</TableCell>
                    <TableCell align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.2)' : '#ffebee' }}>Sett</TableCell>
                    <TableCell align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.2)' : '#ffebee' }}>Mese</TableCell>
                    
                    {/* Senza Note Sub-headers */}
                    <TableCell align="center" sx={{ borderLeft: 1, borderColor: 'divider' }}>Target</TableCell>
                    <TableCell align="center">Oggi</TableCell>
                    <TableCell align="center">Sett</TableCell>
                    <TableCell align="center">Mese</TableCell>
                    
                    {/* Totale Sub-headers */}
                    <TableCell align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'action.hover' : '#f5f5f5', borderLeft: 2, borderColor: 'divider', fontWeight: 'bold' }}>Target</TableCell>
                    <TableCell align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'action.hover' : '#f5f5f5', fontWeight: 'bold' }}>Oggi</TableCell>
                    <TableCell align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'action.hover' : '#f5f5f5', fontWeight: 'bold' }}>Sett</TableCell>
                    <TableCell align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'action.hover' : '#f5f5f5', fontWeight: 'bold' }}>Mese</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adminSummaryData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={21} align="center">
                        Nessuna attività registrata per questo giorno.
                      </TableCell>
                    </TableRow>
                  ) : (
                    adminSummaryData.map((row) => (
                      <AdminSummaryRow key={row.user_id} row={row} targets={targetsInfo?.perCategory} />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleGeneratePdf} startIcon={<DownloadIcon />} color="primary">
            PDF
          </Button>
          <Button onClick={() => setAdminSummaryOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function AdminSummaryRow({ row, targets }) {
    // Group stati by category
    const categories = {
      IN_REGOLA: { label: 'In regola', daily_target: targets?.IN_REGOLA || 0, daily: 0, weekly: 0, monthly: 0 },
      ATTENZIONE: { label: 'Attenzione', daily_target: targets?.ATTENZIONE || 0, daily: 0, weekly: 0, monthly: 0 },
      CRITICA: { label: 'Critica', daily_target: targets?.CRITICA || 0, daily: 0, weekly: 0, monthly: 0 },
      SENZA_NOTE: { label: 'Senza note', daily_target: targets?.SENZA_NOTE || 0, daily: 0, weekly: 0, monthly: 0 },
    };
  
    (row.stati || []).forEach((st) => {
      const name = (st.name || '').toLowerCase();
      let key = 'SENZA_NOTE';
      if (name.startsWith('in regola')) key = 'IN_REGOLA';
      else if (name.startsWith('attenzione')) key = 'ATTENZIONE';
      else if (name.startsWith('critica')) key = 'CRITICA';

      const stTarget = st.daily_target != null ? Number(st.daily_target) : null;
      if (Number.isFinite(stTarget) && stTarget >= 0) {
        categories[key].daily_target = stTarget;
      }
      
      categories[key].daily += st.daily_count || 0;
      categories[key].weekly += st.weekly_count || 0;
      categories[key].monthly += st.monthly_count || 0;
    });
  
    const getStatusIcon = (val, target) => {
      if (target <= 0) return null; // Se target è 0 o negativo, non mostriamo icone (oppure mostriamo Check se vogliamo indicare che "non c'è problema")
      // User requested: "icona x se non raggiunto o icona v se raggiunto"
      return val >= target ? (
        <CheckCircleIcon color="success" fontSize="small" />
      ) : (
        <CancelIcon color="error" fontSize="small" />
      );
    };
  
    const totalWeekly = Object.values(categories).reduce((a, b) => a + b.weekly, 0);
    const totalMonthly = Object.values(categories).reduce((a, b) => a + b.monthly, 0);
    const totalTarget = Object.values(categories).reduce((a, b) => a + b.daily_target, 0);
  
    // Helper to render cells for a category
    const renderCategoryCells = (catKey, categoryType) => {
      const data = categories[catKey];
      
      const getBgColor = (theme) => {
        const mode = theme.palette.mode;
        switch (categoryType) {
          case 'success': return mode === 'dark' ? 'rgba(46, 125, 50, 0.2)' : '#f1f8e9';
          case 'warning': return mode === 'dark' ? 'rgba(237, 108, 2, 0.2)' : '#fff3e0';
          case 'error': return mode === 'dark' ? 'rgba(211, 47, 47, 0.2)' : '#ffebee';
          default: return 'inherit';
        }
      };

      return (
        <React.Fragment>
          <TableCell align="center" sx={{ bgcolor: getBgColor, borderLeft: 1, borderColor: 'divider' }}>
            {data.daily_target}
          </TableCell>
          <TableCell align="center" sx={{ bgcolor: getBgColor }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              {data.daily}
              {getStatusIcon(data.daily, data.daily_target)}
            </Box>
          </TableCell>
          <TableCell align="center" sx={{ bgcolor: getBgColor }}>{data.weekly}</TableCell>
          <TableCell align="center" sx={{ bgcolor: getBgColor }}>{data.monthly}</TableCell>
        </React.Fragment>
      );
    };
  
    return (
      <TableRow hover>
        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
          {row.user_name}
        </TableCell>
        
        {/* IN REGOLA - Light Green background */}
        {renderCategoryCells('IN_REGOLA', 'success')}
        
        {/* ATTENZIONE - Light Orange background */}
        {renderCategoryCells('ATTENZIONE', 'warning')}
        
        {/* CRITICA - Light Red background */}
        {renderCategoryCells('CRITICA', 'error')}
        
        {/* SENZA NOTE */}
        {renderCategoryCells('SENZA_NOTE', 'default')}
        
        {/* TOTALE */}
        <TableCell align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'action.hover' : '#f5f5f5', borderLeft: 2, borderColor: 'divider', fontWeight: 'bold' }}>
          {totalTarget}
        </TableCell>
        <TableCell align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'action.hover' : '#f5f5f5', fontWeight: 'bold' }}>
          {Object.values(categories).reduce((a, b) => a + b.daily, 0)}
        </TableCell>
        <TableCell align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'action.hover' : '#f5f5f5', fontWeight: 'bold' }}>
          {totalWeekly}
        </TableCell>
        <TableCell align="center" sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'action.hover' : '#f5f5f5', fontWeight: 'bold' }}>
          {totalMonthly}
        </TableCell>
      </TableRow>
    );
}

export default AttivitaSintesiMensileInner;
